import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface OnboardingStatus {
  complete: boolean;
  missing_required: string[];
  missing_recommended: string[];
  current_values: {
    bodyweight: number | null;
    bodyfat: number | null;
    height: number | null;
    sex: number | null;
    age: number | null;
    date_of_birth: string | null;
    pal_factor: number | null;
    physical_load: string | null;
    nutrition_goal: string | null;
    calorie_adjustment_kcal: number | null;
    /**
     * Aktive Grundumsatz-Formel aus der Energie-Zeitleiste. null = noch keine
     * Einstellung; der Assistent waehlt dann die Bestandsformel vor.
     */
    bmr_method: string | null;
  };
  physical_load_options: Record<string, { target_pro: number; target_cho: number }>;
  /**
   * PAL-Stufen des Backends. Der Wizard rendert aus den generierten Konstanten
   * (`PAL` in universal/constants/analysis-constants) — dieselbe SSOT, nur ohne
   * Roundtrip. Das Feld bleibt für API-Clients ohne Zugriff darauf.
   */
  pal_options: Array<{
    key: string;
    pal_dge: number;
    factor: number;
    steps_min: number | null;
    steps_max: number | null;
    label: string;
  }>;
}

export interface OnboardingSavePayload {
  bodyweight?: number;
  bodyfat?: number;
  height?: number;
  sex?: number;
  date_of_birth?: string;
  pal_factor?: number;
  physical_load?: string;
  nutrition_goal?: string;
  calorie_adjustment_kcal?: number;
  /** Gewaehlte Grundumsatz-Formel (Schluessel aus BMR.methods). */
  bmr_method?: string;
  /** Wizard-BfMethod ('ten_haaf'|'jp3'|'jp7'|'bia'|'manual'). Backend mappt 'manual'→'direct'. */
  source_method?: string;
  /** Caliper-Rohwerte in mm (nur fuer jp3/jp7 sinnvoll). */
  sites_mm?: Record<string, number>;
}

export interface OnboardingSaveResult {
  ok: boolean;
  message?: string;
  computed?: {
    base_bmr_kcal: number;
    tef_kcal: number;
    bmr_incl_tef_kcal: number;
    neat_kcal: number;
    eat_kcal: number;
    tdee_kcal: number;
    target_kcal: number;
    calorie_adjustment_kcal: number;
    cho_tdee_g: number;
    pro_tdee_g: number;
    fat_tdee_g: number;
    cho_tdee_g_per_kg?: number;
    pro_tdee_g_per_kg?: number;
    fat_tdee_g_per_kg?: number;
    physical_load: string;
    pal_label: string;
    nutrition_goal: string;
  };
}

export interface BodyfatEstimate {
  bodyfat_percent: number;
  method: string;
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private base = `${environment.apiUrl}nutrition/onboarding`;

  constructor(private http: HttpClient) {}

  getStatus(): Observable<OnboardingStatus> {
    return this.http.get<OnboardingStatus>(`${this.base}/status/`);
  }

  save(payload: OnboardingSavePayload): Observable<OnboardingSaveResult> {
    return this.http.post<OnboardingSaveResult>(`${this.base}/save/`, payload);
  }

  estimateBodyfat(body: {
    method: string;
    weight_kg?: number;
    height_cm?: number;
    age?: number;
    sex?: number;
    sites_mm?: Record<string, number>;
  }): Observable<BodyfatEstimate> {
    return this.http.post<BodyfatEstimate>(`${this.base}/estimate-bodyfat/`, body);
  }
}
